import { useEffect, useMemo, useState } from "react";
import { Copy, Check, Download, ShieldCheck, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "ix.admin.token";

/**
 * AdminSources — minimal token-gated board that visualises attribution.
 * Token is remembered in localStorage so investors don't re-enter it.
 */
export default function AdminSources() {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch (_) { return ""; }
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchData = async (t = token) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/waitlist/sources?token=${encodeURIComponent(t)}`);
      if (r.status === 401) {
        setError("Wrong token. Ask a founder.");
        setData(null);
        return;
      }
      if (!r.ok) throw new Error("http");
      const d = await r.json();
      setData(d);
      try { localStorage.setItem(STORAGE_KEY, t); } catch (_) { /* ignore */ }
    } catch {
      setError("Couldn't reach the server. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData(token);
  }, []);

  const rows = data?.buckets || [];
  const total = data?.total_signups || 0;

  const tsv = useMemo(() => {
    if (!rows.length) return "";
    const header = "source\tsignups\tfounders\tshare_pct";
    const body = rows.map((r) => {
      const share = total ? ((r.signups / total) * 100).toFixed(1) : "0.0";
      return `${r.source}\t${r.signups}\t${r.founders}\t${share}`;
    }).join("\n");
    return header + "\n" + body;
  }, [rows, total]);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(tsv);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) { /* silent */ }
  };

  return (
    <main className="min-h-screen bg-canvas pt-32 md:pt-40 pb-24" data-testid="admin-sources-page">
      <div className="container-x max-w-5xl">
        <div className="flex items-center gap-2 label-eyebrow">
          <ShieldCheck className="w-4 h-4 text-forest" strokeWidth={1.75} />
          Admin · attribution
        </div>
        <h1 className="mt-3 font-display font-medium text-forest text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95]">
          Waitlist <span className="italic font-serif">sources.</span>
        </h1>
        <p className="mt-4 text-ink/65 max-w-xl">
          Which corridors and campaigns drive Founder seats? Paste the admin token, hit refresh, then copy the table into your deck.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <label className="flex-1 block">
            <span className="text-[10px] tracking-[0.22em] uppercase text-ink/50 font-semibold">Admin token</span>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-forest/15 bg-white px-4 py-3 text-forest font-mono focus:outline-none focus:border-forest/40"
              placeholder="paste your token"
              data-testid="admin-token-input"
            />
          </label>
          <button
            onClick={() => fetchData()}
            disabled={!token || loading}
            className="btn-accent justify-center sm:w-40 disabled:opacity-60"
            data-testid="admin-load"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load board"}
          </button>
        </div>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-300/40 bg-red-50 text-red-700 px-4 py-3 text-sm" data-testid="admin-error">
            {error}
          </div>
        )}

        {data && (
          <div className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="label-eyebrow">
                {total.toLocaleString()} total signups · {rows.length} sources
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={copyAll}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-forest/15 text-forest text-sm hover:bg-forest/[0.04] transition-colors"
                  data-testid="admin-copy-tsv"
                >
                  {copied ? <Check className="w-4 h-4 text-forest" strokeWidth={2.5} /> : <Copy className="w-4 h-4" strokeWidth={2} />}
                  {copied ? "Copied" : "Copy TSV"}
                </button>
                <a
                  href={`${API}/api/waitlist/sources?token=${encodeURIComponent(token)}&format=csv`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-forest text-canvas text-sm hover:bg-forest/90 transition-colors"
                  data-testid="admin-download-csv"
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  Download CSV
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-forest/12 overflow-hidden bg-white">
              <div className="grid grid-cols-[1fr_repeat(3,auto)] gap-x-6 gap-y-0 px-5 py-3 bg-surface text-[10px] tracking-[0.22em] uppercase font-bold text-forest border-b border-forest/12">
                <div>Source</div>
                <div className="text-right">Signups</div>
                <div className="text-right">Founders</div>
                <div className="text-right">Share</div>
              </div>
              {rows.map((r) => {
                const share = total ? (r.signups / total) * 100 : 0;
                return (
                  <div
                    key={r.source}
                    className="grid grid-cols-[1fr_repeat(3,auto)] gap-x-6 items-center px-5 py-3 border-b border-forest/8 last:border-b-0 hover:bg-lime/5 transition-colors"
                    data-testid={`admin-row-${r.source}`}
                  >
                    <div className="font-mono text-sm text-forest truncate">{r.source}</div>
                    <div className="font-display text-lg text-forest text-right tabular-nums">{r.signups}</div>
                    <div className="font-display text-lg text-forest text-right tabular-nums">{r.founders}</div>
                    <div className="text-right w-32">
                      <div className="inline-flex items-center gap-2 w-full">
                        <div className="flex-1 h-1.5 rounded-full bg-forest/10 overflow-hidden">
                          <div className="h-full bg-lime" style={{ width: `${Math.min(100, share)}%` }} />
                        </div>
                        <span className="font-mono text-[11px] text-ink/60 tabular-nums shrink-0">{share.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
